import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import EditCompanyModal from "../../pages/Company/EditCompany";
import { Modal } from "../../components/ui/modal";
import Loader from "../../components/common/Loader";
// import PageMeta from "../../components/common/PageMeta";
// import PageBreadcrumb from "../../components/common/PageBreadCrumb";

interface Company {
  id: string;
  company_name: string;
  email: string;
  phone_number: string;
  contact_number: string;
  address: string;
  company_logo: string;
  gst_type: string | number;
}

const CompanyDetails: React.FC = () => {
  const [company, setCompany] = useState<Company | null>(null);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [isImageModalOpen, setImageModalOpen] = useState(false);
  const [, setImageLoaded] = useState(false); // New state for image loading

  const fetchCompanyData = async () => {
    const token = sessionStorage.getItem("access_token");
    if (!token) {
      toast.error("Authentication error! Please log in.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/companies`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (result.status === 200 && result.data && result.data.length > 0) {
        setCompany(result.data[0]);
      } else {
        toast.error(result.message || "Failed to fetch company data");
      }
    } catch (error) {
      toast.error("Error fetching company data");
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, []);

  const handleEditClick = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setShowEditCompanyModal(true);
  };

  const handleEditModalClose = () => {
    setShowEditCompanyModal(false);
    fetchCompanyData();
  };

  const handleImageModalOpen = () => {
    if (company?.company_logo) {
      const img = new Image();
      img.src = `${company.company_logo}?t=${new Date().getTime()}`;
      img.onload = () => {
        setImageLoaded(true);
        setImageModalOpen(true);
      };
      img.onerror = () => {
        setImageLoaded(false);
        setImageModalOpen(true); // Open modal with fallback image
      };
    }
  };

  const handleImageModalClose = () => {
    setImageModalOpen(false);
    setImageLoaded(false); // Reset image loaded state
  };

  const getGstTypeName = (gstType: string | number) => {
    const types = [
      { id: "1", type_name: "CGST+SGST" },
      { id: "2", type_name: "IGST" },
    ];
    return (
      types.find((type) => type.id === gstType.toString())?.type_name || gstType
    );
  };

  const getInitials = (companyName: string) => {
    const words = companyName.split(" ").filter((word) => word);
    return words.length > 1
      ? `${words[0][0]}${words[1][0]}`.toUpperCase()
      : companyName.slice(0, 2).toUpperCase();
  };

  return (
    <>
      {/* <PageMeta
                title="Company Dashboard"
                description="This is the Company Dashboard page"
            /> */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] lg:p-6">
        <h3 className="mb-5 text-xl font-semibold text-gray-800 dark:text-white/90 lg:mb-7">
          Company Details
        </h3>
        <div className="space-y-6">
          {loading ? (
            <div className="flex justify-center items-center py-8 min-h-[50vh]">
              <Loader />
            </div>
          ) : company ? (
            <>
              {/* Company Logo Card */}
              <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
                  <div className="flex flex-col items-center w-full gap-6 xl:flex-row">
                    {company.company_logo ? (
                      <img
                        src={`${
                          company.company_logo
                        }?t=${new Date().getTime()}`}
                        alt="Company Logo"
                        className="w-20 h-20 object-cover rounded-full cursor-pointer"
                        onClick={handleImageModalOpen}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const nextSibling = e.currentTarget.nextSibling;
                          if (nextSibling instanceof HTMLElement) {
                            nextSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : (
                      <div className="w-20 h-20 flex items-center justify-center text-3xl font-bold text-white bg-blue-500 rounded-full">
                        {getInitials(company.company_name)}
                      </div>
                    )}
                    <div className="order-3 xl:order-2">
                      <h4 className="mb-2 text-lg font-semibold text-center text-gray-800 dark:text-white/90 xl:text-left">
                        {company.company_name}
                      </h4>
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Info Card */}
              <div className="p-5 border border-gray-200 rounded-2xl dark:border-gray-800 lg:p-6">
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between">
                  <div className="w-full">
                    <h4 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-4">
                      Company Information 
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {(
                        [
                          "company_name",
                          "email",
                          "phone_number",
                          "contact_number",
                          "gst_type",
                          "address",
                        ] as const
                      ).map((field) => (
                        <div key={field}>
                          <p
                            className="text-sm font-medium text-gray-500 dark:text-gray-400"
                            style={{ fontSize: "14px" }}
                          >
                            {field === "gst_type"
                              ? "GST Type"
                              : field
                                  .replace("_", " ")
                                  .replace(/\b\w/g, (char) =>
                                    char.toUpperCase()
                                  )}
                          </p>
                          <p className="text-theme-sm text-black dark:text-white">
                            {field === "gst_type"
                              ? getGstTypeName(company.gst_type)
                              : company[field] || "N/A"}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleEditClick(company.id)}
                    className="mt-4 xl:mt-0 flex justify-center items-center gap-2 px-4 py-2 text-sm bg-[#2f6aa7] dark:bg-[#2f6aa7] text-white rounded-lg hover:bg-[#25578a] dark:hover:bg-[#25578a]"
                  >
                    <svg
                      className="fill-current w-4 h-4"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M15.0911 2.78206C14.2125 1.90338 12.7878 1.90338 11.9092 2.78206L4.57524 10.116C4.26682 10.4244 4.0547 10.8158 3.96468 11.2426L3.31231 14.3352C3.25997 14.5833 3.33653 14.841 3.51583 15.0203C3.69512 15.1996 3.95286 15.2761 4.20096 15.2238L7.29355 14.5714C7.72031 14.4814 8.11172 14.2693 8.42013 13.9609L15.7541 6.62695C16.6327 5.74827 16.6327 4.32365 15.7541 3.44497L15.0911 2.78206ZM12.9698 3.84272C13.2627 3.54982 13.7376 3.54982 14.0305 3.84272L14.6934 4.50563C14.9863 4.79852 14.9863 5.2734 14.6934 5.56629L14.044 6.21573L12.3204 4.49215L12.9698 3.84272ZM11.2597 5.55281L5.6359 11.1766C5.53309 11.2794 5.46238 11.4099 5.43238 11.5522L5.01758 13.5185L6.98394 13.1037C7.1262 13.0737 7.25666 13.003 7.35947 12.9002L12.9833 7.27639L11.2597 5.55281Z"
                      />
                    </svg>
                    Edit
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center text-gray-500 dark:text-gray-400 font-semibold text-base p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              No company details available
            </div>
          )}
        </div>

        {selectedCompanyId && showEditCompanyModal && (
          <EditCompanyModal
            isOpen={showEditCompanyModal}
            onClose={handleEditModalClose}
            state={{ company_id: selectedCompanyId }}
          />
        )}

        {company && (
          <Modal
            isOpen={isImageModalOpen}
            onClose={handleImageModalClose}
            className="w-[90%] max-w-lg bg-white dark:bg-gray-900 p-6 rounded-lg shadow-xl mx-auto text-center"
          >
            <div className="flex justify-center">
              <img
                src={company.company_logo}
                alt="Company Logo Enlarged"
                className="w-full h-auto max-h-[70vh] rounded object-contain"
              />
            </div>
          </Modal>
        )}
      </div>
    </>
  );
};

export default CompanyDetails;
